/**
 * Expand the absence-reason prototype (late-cancel / no-show and cancelled session notes)
 * into one why-question per participant.
 * Does not stamp GROUP_NOTE_PATIENT_SECTION_EXT (that would trigger group fan-out).
 */
import type { Questionnaire, QuestionnaireItem } from '@medplum/fhirtypes';
import { ABSENCE_REASON_PROTOTYPE_LINK_ID } from '@unify/fhir-model/constants';
import type { ParticipantAttendanceStatus } from './participantAttendance';

export type AbsenceNoteParticipant = {
  patientId: string;
  displayName: string;
};

function suffixLinkId(linkId: string | undefined, patientId: string): string {
  const base = (linkId ?? '').trim() || 'item';
  if (base.endsWith(`.${patientId}`)) return base;
  return `${base}.${patientId}`;
}

function absenceQuestionText(
  displayName: string,
  status: ParticipantAttendanceStatus | string | undefined,
): string {
  const name = displayName.trim() || 'this client';
  if (status === 'client-no-show') {
    return `Why was ${name}'s session a no show?`;
  }
  if (status === 'client-cancelled') {
    return `Why did ${name} cancel the session?`;
  }
  if (status === 'clinician-cancelled') {
    return `Why was ${name}'s session cancelled by the clinician?`;
  }
  return `Why was ${name}'s session late cancelled?`;
}

function buildAbsenceQuestion(
  prototype: QuestionnaireItem,
  participant: AbsenceNoteParticipant,
  status: ParticipantAttendanceStatus | string | undefined,
): QuestionnaireItem {
  const child = prototype.item?.[0];
  const text = absenceQuestionText(participant.displayName, status);
  if (child?.type === 'text') {
    return {
      ...child,
      linkId: suffixLinkId(child.linkId ?? 'absence-reason', participant.patientId),
      text,
      required: true,
    };
  }
  return {
    linkId: suffixLinkId(prototype.linkId, participant.patientId),
    type: 'text',
    text,
    required: true,
  };
}

export function expandAbsenceReasonPrototype(
  items: QuestionnaireItem[] | undefined,
  participants: AbsenceNoteParticipant[],
  attendanceByPatientId: Record<string, ParticipantAttendanceStatus | string | undefined>,
): QuestionnaireItem[] | undefined {
  if (!items?.length) return items;
  const out: QuestionnaireItem[] = [];
  for (const item of items) {
    if (item.linkId !== ABSENCE_REASON_PROTOTYPE_LINK_ID) {
      out.push({
        ...item,
        item: expandAbsenceReasonPrototype(item.item, participants, attendanceByPatientId),
      });
      continue;
    }
    for (const participant of participants) {
      out.push(buildAbsenceQuestion(item, participant, attendanceByPatientId[participant.patientId]));
    }
  }
  return out;
}

export function expandAbsenceNoteQuestionnaire(
  questionnaire: Questionnaire,
  participants: AbsenceNoteParticipant[],
  attendanceByPatientId: Record<string, ParticipantAttendanceStatus | string | undefined>,
): Questionnaire {
  const hasPrototype = questionnaire.item?.some((i) => i.linkId === ABSENCE_REASON_PROTOTYPE_LINK_ID);
  if (!hasPrototype || participants.length === 0) return questionnaire;
  return {
    ...questionnaire,
    item: expandAbsenceReasonPrototype(questionnaire.item, participants, attendanceByPatientId),
  };
}
